import React from 'react'
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  TimeScale,
  Tooltip,
  Plugin,
  ChartOptions,
  ChartData,
  ChartDataset
} from 'chart.js'
import { Chart, } from 'react-chartjs-2'
import annotationPlugin from 'chartjs-plugin-annotation'
import 'chartjs-adapter-moment'
import { useGlobalState } from 'state'
import { SuijinStatus } from 'common/consts'
import { ObservatoryRow, SuijinValue } from 'common/types'
import dayjs from 'dayjs'

ChartJS.register(
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  TimeScale,
  Tooltip,
  annotationPlugin
)

type SuijinChartProps = {
  observatory: ObservatoryRow
  height: number
  timeSeries: SuijinValue[]
  waterBoxLevel: number | null
}

export const SuijinChart = ({observatory, height, timeSeries, waterBoxLevel}: SuijinChartProps) => {
  const state = useGlobalState()
  const [chartData, setChartData] = React.useState<ChartData>({labels: [], datasets: []})
  const [chartOptions, setChartOptions] = React.useState<ChartOptions>({})
  const chartRef = React.useRef<ChartJS>(null)

  const maxLevel = 2
  const minLevel = -0.5

  React.useEffect(() => {
    const handleVisibilityChange = () => {
      chartRef.current?.update()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const plugins: Plugin[] = React.useMemo(() => {
    let landform = observatory.landform === undefined ? [] : observatory.landform
    if (landform.length === 0) {
      landform = [[0, 0], [100, 0]]
    }

    return [{
      id: 'landform',
      beforeDatasetsDraw: chart => {
        if (!chart.scales.xDate || !chart.scales.yLevels) {
          return
        }
        const ctx = chart.ctx
        const xAxe = chart.scales.xDate
        const yAxe = chart.scales.yLevels
        const arrX = landform.map(lf => lf[0])
        const arrY = landform.map(lf => lf[1])
        const minX = Math.min(...arrX)
        const maxX = Math.max(...arrX)
        const maxY = Math.max(...arrY)

        const getX = (baseX: number): number => {
          return ((baseX - minX) / (maxX - minX)) * xAxe.width + xAxe.left
        }

        const getY = (baseY: number): number => {
          return ((yAxe.max - yAxe.min - (baseY - yAxe.min)) / (yAxe.max - yAxe.min)) * yAxe.height + yAxe.top
        }

        // landform
        ctx.beginPath()
        ctx.moveTo(xAxe.left, yAxe.bottom)
        for (let i = 0; i < landform.length; i++) {
          ctx.lineTo(getX(landform[i][0]), getY(landform[i][1]))
        }
        ctx.lineTo(xAxe.right, yAxe.bottom)
        ctx.lineTo(xAxe.left, yAxe.bottom)
        ctx.closePath()
        ctx.fillStyle = '#cf9e80'
        ctx.fill()

        // 道路面
        const road_offset = 0.3
        const road_y = getY(maxY - road_offset / 2)
        for (let i = 1; i < landform.length; i++) {
          if (landform[i - 1][1] !== maxY || landform[i][1] !== maxY) continue
          ctx.strokeStyle = '#9e9e9e'
          ctx.lineWidth = getY(0) - getY(road_offset)
          ctx.beginPath()
          ctx.setLineDash([])
          ctx.moveTo(getX(landform[i - 1][0]), road_y)
          ctx.lineTo(getX(landform[i][0]), road_y)
          ctx.stroke()
        }

        // 線形グラデーションを作成
        if (observatory.sensors && waterBoxLevel !== null) {
          const sensorMaxLevel = Math.max(...observatory.sensors.map(s => s.level))
          if (sensorMaxLevel === waterBoxLevel) {
            const gradientHeight = 0.6
            const gradient = ctx.createLinearGradient(
              0,
              getY(gradientHeight),
              0,
              getY(sensorMaxLevel)
            )
            gradient.addColorStop(0, '#3D8AFF00')
            gradient.addColorStop(1, '#3D8AFFFF')
            ctx.fillStyle = gradient
            ctx.fillRect(
              xAxe.left,
              getY(gradientHeight),
              xAxe.right - xAxe.left,
              getY(sensorMaxLevel) - getY(gradientHeight)
            )
          }
        }

        // sensor lines
        if (observatory.sensors !== undefined && observatory.sensors.length > 0) {
          observatory.sensors.forEach((sensor, i) => {
            ctx.save()
            let lineColor = '#000000'
            switch (i) {
            case 0: lineColor = SuijinStatus.FLOOD4.color; break
            case 1: lineColor = SuijinStatus.FLOOD3.color; break
            case 2: lineColor = SuijinStatus.FLOOD2.color; break
            case 3: lineColor = SuijinStatus.FLOOD1.color; break
            }
            // ctx.setLineDash([5, 5])
            ctx.strokeStyle = lineColor
            ctx.lineWidth = 1
            const yPosition = yAxe.getPixelForValue(sensor.level)
            ctx.beginPath()
            ctx.moveTo(xAxe.left, yPosition)
            ctx.lineTo(xAxe.right, yPosition)
            ctx.stroke()
          })
          ctx.restore()
        }

        // 画像
        const roadLandformXs = landform
          .filter(lf => lf[1] === maxY)
          .map(lf => lf[0])
        if (state.app.images.suijinChartCar) {
          const img = state.app.images.suijinChartCar
          const h = getY(0) - getY(1.5)
          const w = h * (img.width / img.height)
          ctx.drawImage(
            img,
            getX(Math.min(...roadLandformXs) + 2),
            getY(maxY) - h,
            w,
            h
          )
        }
      }
    }]
  }, [observatory, waterBoxLevel])

  React.useEffect(() => {
    loadTimeSeries()
  }, [timeSeries, waterBoxLevel])

  const loadTimeSeries = () => {
    const labels: string[] = timeSeries.map(ts => {
      return dayjs.utc(ts.date).local().format('YYYY/MM/DD HH:mm')
    })

    const datasets: ChartDataset[] = []
    if (observatory.sensors) {
      observatory.sensors.forEach(sensor => {
        const data = timeSeries.map(ts => ts.sensorLevels[sensor.id])
        datasets.push({
          type: 'line' as const,
          label: '',
          pointBackgroundColor: 'white',
          fill: false,
          xAxisID: 'xDate',
          yAxisID: 'yLevels',
          borderWidth: 1,
          borderColor: '#01163F',
          backgroundColor: '#01163F',
          pointBorderColor: '#01163F',
          pointRadius: 3,
          pointHitRadius: 0,
          data: data,
          spanGaps: true
        })
      })
    }

    setChartOptions({
      responsive: true,
      interaction: {
        mode: 'index',
        intersect: false
      },
      scales: {
        xDate: {
          type: 'time',
          time: {
            tooltipFormat: 'MM/DD HH:mm',
            parser: 'YYYY/MM/DD HH:mm',
            unit: 'hour',
            minUnit: 'minute',
            displayFormats: {
              'hour': 'HH:mm',
            }
          },
          min: state.app.baseDate.subtract(3, 'hours').local().format('YYYY/MM/DD HH:mm'),
          max: state.app.baseDate.add(10, 'minutes').local().format('YYYY/MM/DD HH:mm')
        },
        yLevels: {
          ticks: {
            stepSize: 1
          },
          max: maxLevel,
          min: minLevel,
        }
      },
      plugins: {
        filler: {
          drawTime: 'beforeDatasetsDraw'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || ''
              if (label) {
                label += ': '
              }
              if (context.parsed.y !== null) {
                label += context.parsed.y + 'm'
              }
              return label
            }
          }
        },
        annotation: {
          annotations: {
            nowLine: {
              borderColor: '#FF0000',
              borderWidth: 1.5,
              type: 'line',
              scaleID: 'xDate',
              value: state.app.baseDate.local().format('YYYY/MM/DD HH:mm'),
              label: {
                content: '現在',
                position: 'start',
                display: true
              }
            },
            waterBox: {
              type: 'box',
              drawTime: 'beforeDatasetsDraw',
              yScaleID: 'yLevels',
              yMin: -1,
              yMax: waterBoxLevel !== null ? waterBoxLevel : -1,
              backgroundColor: '#3D8AFF'
            },
          }
        }
      }
    })

    setChartData({
      labels,
      datasets: datasets
    })
  }

  return <Chart ref={chartRef} type='bar' data={chartData} height={height} options={chartOptions} plugins={plugins} redraw />
}
